import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, RefreshCw, User, Video, Phone, Building2 } from 'lucide-react';
import {
  SAMPLE_DASHBOARD_BOOKINGS,
  fetchAdminBookings,
  fetchConsultantBookings,
  formatBookingWhen,
  isSchedulerDemoMode,
  splitTodayAndUpcoming,
} from '../utils/schedulerBookings';
import './DashboardBookings.css';

const LIMIT = 4;

/**
 * Compact today + upcoming bookings for admin / consultant dashboards.
 * variant: 'admin' | 'consultant'
 */
function DashboardBookings({ user, variant = 'admin' }) {
  const isAdmin = variant === 'admin';
  const email = user?.email || '';
  const displayName = user?.displayName || '';

  const [bookings, setBookings] = useState([]);
  const [usingSample, setUsingSample] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const applySample = useCallback(() => {
    setBookings(SAMPLE_DASHBOARD_BOOKINGS);
    setUsingSample(true);
  }, []);

  const load = useCallback(async () => {
    const demo = isSchedulerDemoMode();
    if (demo || (!isAdmin && !email && !displayName)) {
      applySample();
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const list = isAdmin
        ? await fetchAdminBookings()
        : await fetchConsultantBookings({ email, name: displayName });
      if (!list.length) {
        applySample();
      } else {
        setBookings(list);
        setUsingSample(false);
      }
    } catch (err) {
      console.error('DashboardBookings:', err);
      applySample();
    } finally {
      setLoading(false);
    }
  }, [isAdmin, email, displayName, applySample]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const { today, upcoming } = useMemo(() => splitTodayAndUpcoming(bookings), [bookings]);

  const title = isAdmin ? 'Calendar Bookings' : 'My Bookings';
  const subtitle = isAdmin
    ? 'Today and upcoming interviews from Diologic Scheduler'
    : 'Your confirmed interview slots';

  return (
    <div className={`dashboard-bookings ${isAdmin ? 'db-admin' : 'db-consultant'}`}>
      <div className="db-head">
        <div className="db-copy">
          <Calendar className="db-title-icon" size={18} aria-hidden="true" />
          <div className="db-copy-text">
            <h3 className="db-title">{title}</h3>
            <p className="db-sub">{subtitle}</p>
          </div>
        </div>
        <div className="db-actions">
          <button
            type="button"
            className="db-icon-btn"
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
            aria-label="Refresh bookings"
          >
            <RefreshCw size={15} className={loading ? 'spin' : ''} />
          </button>
          <Link to="/calendar" className="db-link">
            View all
          </Link>
        </div>
      </div>

      {usingSample && !loading && (
        <div className="db-sample">Sample bookings for preview — live scheduler data replaces these when available.</div>
      )}

      {loading ? (
        <div className="db-loading">
          <div className="loading-spinner" />
          <span>Loading bookings…</span>
        </div>
      ) : (
        <div className="db-columns">
          <BookingColumn
            label="Today"
            items={today}
            empty="No interviews scheduled for today."
            showConsultant={isAdmin}
            limit={LIMIT}
          />
          <BookingColumn
            label="Upcoming"
            items={upcoming}
            empty="No upcoming interviews."
            showConsultant={isAdmin}
            limit={LIMIT}
          />
        </div>
      )}
    </div>
  );
}

function BookingColumn({ label, items, empty, showConsultant, limit }) {
  const list = items.slice(0, limit);
  return (
    <section className="db-col">
      <div className="db-col-head">
        <h4>{label}</h4>
        <span className="db-count">{items.length}</span>
      </div>
      {list.length === 0 ? (
        <p className="db-empty">{empty}</p>
      ) : (
        <ul className="db-list">
          {list.map((b) => (
            <CompactBooking key={b.id} booking={b} showConsultant={showConsultant} />
          ))}
        </ul>
      )}
      {items.length > limit && (
        <p className="db-more">+{items.length - limit} more on Calendar</p>
      )}
    </section>
  );
}

function CompactBooking({ booking, showConsultant }) {
  const when = formatBookingWhen(booking.start_time, booking.end_time, booking.duration_minutes);
  const CallIcon = booking.call_type === 'Audio' ? Phone : Video;

  return (
    <li className="db-item">
      <div className="db-when">
        <span><Clock size={13} /> {when.time}</span>
        <span className="db-date">{when.date}</span>
      </div>
      <div className="db-body">
        {showConsultant && (
          <div className="db-line">
            <User size={13} />
            <strong>{booking.consultant_name || '—'}</strong>
          </div>
        )}
        <div className="db-line">
          <Building2 size={13} />
          <span>{booking.client_name || '—'}</span>
        </div>
        <div className="db-meta">
          <CallIcon size={12} />
          <span>
            {booking.call_type || 'Call'} · {booking.duration_minutes || 30} min
          </span>
          {booking.vendor_name ? <span className="db-dot">· {booking.vendor_name}</span> : null}
        </div>
      </div>
    </li>
  );
}

export default DashboardBookings;
